import React from 'react';
import {
  Box,
  Button,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Print as PrintIcon } from '@mui/icons-material';
import { printWithFilename } from '../../utils/printUtils';
import '../../styles/print.css';
import {
  SalesCard,
  SalesPageHeader,
  SALES,
  salesPrimaryButtonSx,
  salesTableHeadSx,
} from '../../components/sales/salesUi';

const Section = ({ title, children }) => (
  <Box sx={{ mb: 3.5 }} className="sales-guide-section">
    <Typography
      variant="h6"
      sx={{
        mb: 1.25,
        fontWeight: 700,
        color: SALES.ink,
        pb: 1,
        borderBottom: `2px solid ${SALES.steelSoft}`,
      }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

const SalesGuide = () => {
  const handlePrint = () => {
    printWithFilename('Fab-Works-Sales-Rep-Guide');
  };

  return (
    <Box>
      <Box className="no-print">
        <SalesPageHeader
          title="Sales Rep Guide"
          subtitle="Printable field reference for the Outside Sales Portal"
          actions={
            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              sx={salesPrimaryButtonSx}
            >
              Print Guide
            </Button>
          }
        />
      </Box>

      <SalesCard
        className="sales-guide-print"
        sx={{
          p: 3,
          '@media print': {
            boxShadow: 'none',
            border: 'none',
            p: 0,
            maxWidth: '100%',
          },
        }}
      >
        <Typography
          variant="h4"
          sx={{ mb: 0.5, fontSize: { xs: '1.4rem', sm: '1.75rem' }, fontWeight: 700, color: SALES.ink }}
        >
          Fab Works — Outside Sales Rep Guide
        </Typography>
        <Typography variant="body2" sx={{ color: SALES.muted, mb: 2 }}>
          WorkManager Sales Portal
        </Typography>
        <Divider sx={{ mb: 3, borderColor: SALES.line }} />

        <Section title="1. Getting started">
          <Typography paragraph>
            Sign in with the email and password HR gave you. You will land on <strong>My Leads</strong> in the
            Sales Portal.
          </Typography>
          <Typography paragraph sx={{ mb: 1 }}>
            You only see <strong>your own</strong> leads, activity, and quote requests—not another rep’s.
          </Typography>
          <Table size="small" sx={{ maxWidth: 560 }}>
            <TableHead sx={salesTableHeadSx}>
              <TableRow>
                <TableCell>Menu</TableCell>
                <TableCell>What it’s for</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>My Leads</TableCell>
                <TableCell>Prospects you are working</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Activity Log</TableCell>
                <TableCell>All your phone calls and visits</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Customers</TableCell>
                <TableCell>Existing Fab Works customers (read-only)</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Quote Requests</TableCell>
                <TableCell>Quotes you submit for Admin review</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Guide</TableCell>
                <TableCell>This page (print anytime)</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Profile</TableCell>
                <TableCell>Your info and password change</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Section>

        <Section title="2. Working leads">
          <Typography variant="subtitle2" sx={{ mt: 1 }}>Create a lead</Typography>
          <Typography component="ol" sx={{ pl: 2.5, mt: 0.5, mb: 1.5, '& li': { mb: 0.5 } }}>
            <li>Open <strong>My Leads</strong> → <strong>New Lead</strong>.</li>
            <li>
              Fill the same fields as the paper Sales Leads form: company / address / business info, point of
              contact, lead source, marketing materials / dropoff items, questions / interests / specs, and
              general notes.
            </li>
            <li>Click <strong>Save</strong>.</li>
          </Typography>

          <Typography variant="subtitle2">Update a lead</Typography>
          <Typography paragraph>
            Open the lead from the list, edit fields, set status (<em>new</em>, <em>contacted</em>,{' '}
            <em>quoting</em>, <em>lost</em>, <em>dead</em>), then <strong>Save</strong>. Status becomes{' '}
            <strong>converted</strong> automatically when an Admin approves a quote request for that lead—you
            don’t set that yourself.
          </Typography>

          <Typography variant="subtitle2">Log a call or visit</Typography>
          <Typography paragraph>
            Fastest way: click the blue <strong>Log Call / Visit</strong> button (sidebar, My Leads, Customers, or Activity
            Log). Pick <strong>Existing Lead</strong>, <strong>New Lead</strong>, or{' '}
            <strong>Existing Customer</strong>, then fill in the call details. Choosing New Lead creates the lead
            and the activity together, so you never have to enter the company twice. Use{' '}
            <strong>Save &amp; Log Another</strong> when you are catching up on several stops at once.
          </Typography>
          <Typography paragraph>
            You can also log from the lead detail page → <strong>Activity Log</strong> tab →{' '}
            <strong>Log Call / Visit</strong>.
            Choose type (Phone, Onsite Visit, Email, Other), date, contact name, and notes (required). Optional:
            duration, outcome, next action / date.
          </Typography>
        </Section>

        <Section title="3. Existing customers">
          <Typography paragraph>
            Use <strong>Customers</strong> to look up accounts and stay in touch. You can search, open a
            customer, view company info and contacts, log a call/visit (only your logs show), and start a quote
            request.
          </Typography>
          <Typography paragraph>
            You cannot edit company records, credit, or financial info. Ask Admin/office staff for those changes.
          </Typography>
        </Section>

        <Section title="4. Quote requests">
          <Typography paragraph>
            You don’t create Quotes directly. You submit a <strong>Quote Request</strong>; an Admin reviews it
            and turns it into a real Quote.
          </Typography>
          <Typography component="ol" sx={{ pl: 2.5, mt: 0.5, mb: 1.5, '& li': { mb: 0.5 } }}>
            <li>
              Go to <strong>Quote Requests</strong> → <strong>New Request</strong>, or start from a Lead /
              Customer page.
            </li>
            <li>Choose <strong>New Lead</strong> or <strong>Existing Customer</strong>.</li>
            <li>Add description, notes, needed-by date, and line items (description, qty, price).</li>
            <li>Use <strong>Save Draft</strong> while working; click <strong>Submit for Review</strong> when ready.</li>
          </Typography>

          <Table size="small" sx={{ maxWidth: 640, mb: 1.5 }}>
            <TableHead sx={salesTableHeadSx}>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>Meaning</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>draft</TableCell>
                <TableCell>Yours; still editable</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>submitted</TableCell>
                <TableCell>Waiting on Admin</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>approved</TableCell>
                <TableCell>Became a real Quote (and a Customer, if it was a new lead)</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>rejected</TableCell>
                <TableCell>Sent back with notes—edit and resubmit</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>withdrawn</TableCell>
                <TableCell>You pulled it back</TableCell>
              </TableRow>
            </TableBody>
          </Table>
          <Typography paragraph>
            If rejected, open the request, read the notes, fix it, and submit again.
          </Typography>
        </Section>

        <Section title="5. After Admin approval">
          <Typography paragraph>
            <strong>If the request was for a new lead:</strong> the lead becomes converted, a real Customer is
            created, you are credited as <strong>Sourced By</strong> (who brought them in), and{' '}
            <strong>Account Rep</strong> is usually you unless Admin assigns someone else.
          </Typography>
          <Typography paragraph>
            A real Quote is always created in the main system and attributed to you as the salesperson. You won’t
            open the Quote PDF in this portal yet—you’ll see the request marked <strong>approved</strong>.
            Shop/sales staff finish the Quote in the main app.
          </Typography>
        </Section>

        <Section title="6. Daily tips">
          <Typography component="ol" sx={{ pl: 2.5, mt: 0.5, mb: 0, '& li': { mb: 0.5 } }}>
            <li>Create the Lead the same day you visit.</li>
            <li>Log every phone and onsite contact.</li>
            <li>Keep lead status current (contacted → quoting).</li>
            <li>Submit quote requests with clear specs and line items so Admin can approve quickly.</li>
            <li>Use Customers for existing accounts; use Leads for prospects who aren’t customers yet.</li>
          </Typography>
        </Section>

        <Section title="7. Profile & password">
          <Typography paragraph>
            Open <strong>Profile</strong> to change your password (current password + new password, at least 6
            characters). If you can’t sign in, contact Admin/HR—they manage accounts and roles.
          </Typography>
        </Section>

        <Section title="8. FAQ">
          <Typography paragraph>
            <strong>Can I see another Outside Sales Rep’s leads?</strong> No.
          </Typography>
          <Typography paragraph>
            <strong>Can I edit an existing customer’s address or credit?</strong> No—ask Admin/office staff.
          </Typography>
          <Typography paragraph>
            <strong>Why can’t I open Quotes or Orders?</strong> Those stay in the main app. Your portal stops at
            Quote Requests.
          </Typography>
          <Typography paragraph sx={{ mb: 0 }}>
            <strong>Who becomes the customer’s “Rep”?</strong> Sourced By = credit for bringing them in (usually
            you). Account Rep = ongoing relationship owner (can be you, someone else, or empty)—set at approval
            and editable later by Admin.
          </Typography>
        </Section>
      </SalesCard>
    </Box>
  );
};

export default SalesGuide;
